import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Transfer, Select } from "antd";
import axios from "axios";
import { IAvaliacao } from "./VisaoAnualGrid";
import { fetchInstrumentos } from "../pre-cadastro-instrumento";
import { getEspecialidades } from "../../../../helpers/api";
import { getAgendamentoSalaCompletoDetalhes } from "helpers/api/agendamentoSala";

interface EditReavaliacaoModalProps {
  show: boolean;
  onHide: () => void;
  avaliacao: IAvaliacao;
}

interface Colaborador {
  id: number;
  nome: string;
  aplicador: boolean;
  apelido: string;
}

interface InstrumentoComChave {
  key: string;
  id: number;
  instrumento: string;
}

export default function EditReavaliacaoModal({
  show,
  onHide,
  avaliacao,
}: EditReavaliacaoModalProps) {
  const [erro, setErro] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [formData, setFormData] = useState("");
  const [instrumentoKey, setInstrumentoKey] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [instrumentosDisponiveis, setInstrumentosDisponiveis] = useState<
    InstrumentoComChave[]
  >([]);
  const [aplicadoresPorInstrumento, setAplicadoresPorInstrumento] = useState<
    Record<number, string[]>
  >({});
  const [especialidadesSelecionadas, setEspecialidadesSelecionadas] = useState<number[]>(
  (avaliacao?.especialidades ?? []).map((a) => a.id)
);
  const [especialidades, setEspecialidades] = useState<
    {
      id: number;
      descricao: string;
    }[]
  >([]);

  useEffect(() => {
    const fetchEspecialidades = async () => {
      const { data } = await getEspecialidades("?pageSize=100");
      setEspecialidades(data.result);
    };
    fetchEspecialidades();
  }, []);

  useEffect(() => {
    if (!show || !avaliacao) return;

    const carregar = async () => {
      try {
        setLoading(true);
        const [colaboradoresResponse, instrumentosResponse] = await Promise.all(
          [
            axios.get("/colaborador?pageSize=1000000&no_include=true"),
            fetchInstrumentos(),
          ],
        );
        setEspecialidadesSelecionadas((avaliacao?.especialidades ?? []).map((a) => a.id))
        setColaboradores(colaboradoresResponse.data.result || []);

        setInstrumentosDisponiveis(
          (instrumentosResponse.data || []).map((i: any) => ({
            key: String(i.id),
            id: i.id,
            instrumento: i.instrumento,
          })),
        );

        const data = avaliacao.avaliacao_reavaliacao?.slice(0, 10) || "";

        setFormData(data);

        const instrumentosSelecionados = [
          ...new Set(avaliacao.relacoes.map((r) => String(r.instrumento.id))),
        ];

        setInstrumentoKey(instrumentosSelecionados);

        const agrupados: Record<number, string[]> = {};

        avaliacao.relacoes.forEach((r) => {
          if (!agrupados[r.instrumento.id]) {
            agrupados[r.instrumento.id] = [];
          }

          if (r.aplicador?.id != null) {
            agrupados[r.instrumento.id].push(String(r.aplicador.id));
          }
        });

        setAplicadoresPorInstrumento(agrupados);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setErro("Erro ao carregar dados.");
      }
    };

    carregar();
  }, [show, avaliacao]);

  const getInstrumentosValidos = () => {
    return instrumentosDisponiveis.filter((i) =>
      instrumentoKey.includes(i.key),
    );
  };

  const handleInstrumentosChange = (keys: string[]) => {
    setInstrumentoKey(keys);

    const novoMapeamento = { ...aplicadoresPorInstrumento };

    instrumentosDisponiveis.forEach((item) => {
      if (!keys.includes(item.key)) {
        delete novoMapeamento[item.id];
      } else if (!novoMapeamento[item.id]) {
        novoMapeamento[item.id] = [];
      }
    });

    setAplicadoresPorInstrumento(novoMapeamento);
  };

  const handleAplicadoresChange = (
    instrumentoId: number,
    aplicadores: string[],
  ) => {
    setAplicadoresPorInstrumento((prev) => ({
      ...prev,
      [instrumentoId]: aplicadores,
    }));
  };

  const temTodosAplicadoresPreenchidos = () => {
    return getInstrumentosValidos().every((i) => {
      const aplicadores = aplicadoresPorInstrumento[i.id];
      return aplicadores && aplicadores.length > 0;
    });
  };

  const handleSalvar = async () => {
    try {
      setSaving(true);
      setErro(null);

      const instrumentos = getInstrumentosValidos().map((i) => ({
        instrumento_id: i.id,
        aplicadores: aplicadoresPorInstrumento[i.id] || [],
      }));

      await axios.put(
        `/relatorio-parcial/${avaliacao.avaliacao_id}/avaliacao-reavaliacao/`,
        {
          avaliacao_reavalicao: formData,
          especialidadesSelecionadas,
          instrumentos,
        },
      );

      onHide();
      setEspecialidadesSelecionadas([])
    } catch (err: any) {

      setErro(err?.response?.data?.error || "Erro ao atualizar avaliação.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={()=> {
        onHide()
        setEspecialidadesSelecionadas([])
      }} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <span style={{ fontWeight: "bold" }}>
            Editar Avaliação nº{avaliacao?.avaliacao_id}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Modal.Title
          style={{
            marginBottom: "3%",
          }}
        >
          Paciente:
          <span
            style={{
              fontWeight: "bold",
            }}
          >
            {` ${avaliacao?.paciente || ""}`}
          </span>
        </Modal.Title>
        {loading ? (
          <>Carregando...</>
        ) : (
          <>
            <Form.Group style={{ marginBottom: 20 }}>
              <Form.Label>Data da Avaliação</Form.Label>

              <Form.Control
                type="date"
                value={
                  new Date(formData).getTime() === new Date(0).getTime()
                    ? new Date().toISOString
                    : formData
                }
                onChange={(e) => setFormData(e.target.value)}
              />
            </Form.Group>

            <Form.Group style={{ marginBottom: "20px" }}>
              <Form.Label
                style={{
                  fontWeight: 600,
                  color: "#2c3e35",
                  marginBottom: "8px",
                }}
              >
                Especialidades
              </Form.Label>
              <Select
                getPopupContainer={(trigger) => trigger.parentElement!}
                style={{ width: "100%" }}
                optionFilterProp="label"
                options={especialidades.map((e) => ({
                  value: e.id,
                  label: e.descricao,
                }))}
                value={especialidadesSelecionadas}
                onChange={setEspecialidadesSelecionadas}
                mode="multiple"
                placeholder="Selecione uma ou mais especialidades"
              />
            </Form.Group>

            <Form.Group style={{ marginBottom: 20 }}>
              <Form.Label>Instrumentos</Form.Label>

              <Transfer
                targetKeys={instrumentoKey}
                onChange={handleInstrumentosChange}
                dataSource={instrumentosDisponiveis}
                render={(item) => item.instrumento}
                listStyle={{
                  width: "100%",
                  height: 250,
                }}
              />
            </Form.Group>

            {getInstrumentosValidos().length > 0 && (
              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Label
                  style={{
                    fontWeight: 600,
                    color: "#2c3e35",
                    marginBottom: "12px",
                  }}
                >
                  Aplicadores por Instrumento
                </Form.Label>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {getInstrumentosValidos().map((instrumento) => (
                    <div
                      key={instrumento.id}
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        padding: "12px",
                        border: "1px solid #ebebeb",
                      }}
                    >
                      <div style={{ marginBottom: "8px" }}>
                        <span
                          style={{
                            fontWeight: 500,
                            color: "#2c3e35",
                            fontSize: "0.95rem",
                          }}
                        >
                          {instrumento.instrumento}
                        </span>
                      </div>

                      <Select
                        optionFilterProp="label"
                        dropdownStyle={{ zIndex: 9999 }}
                        mode="multiple"
                        placeholder="Selecione um ou mais aplicadores"
                        value={aplicadoresPorInstrumento[instrumento.id] || []}
                        onChange={(value) =>
                          handleAplicadoresChange(instrumento.id, value)
                        }
                        style={{ width: "100%" }}
                        options={colaboradores
                          .filter((c: any) => c.aplicador)
                          .map((c) => ({
                            label: c.apelido,
                            value: String(c.id),
                          }))}
                      />
                    </div>
                  ))}
                </div>

                <small
                  style={{
                    color: "#999",
                    marginTop: "8px",
                    display: "block",
                  }}
                >
                  Selecione pelo menos um aplicador para cada instrumento
                </small>
              </Form.Group>
            )}

            {erro && (
              <div
                style={{
                  padding: 12,
                  background: "#fef5ec",
                  borderRadius: 6,
                }}
              >
                {erro}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="light"
          onClick={() => {
            onHide();
            setEspecialidadesSelecionadas([]);
          }}
          disabled={saving}
        >
          Cancelar
        </Button>

        <Button
          variant="primary"
          disabled={saving || !formData || !temTodosAplicadoresPreenchidos()}
          onClick={handleSalvar}
        >
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
